import {
  BankOutlined,
  CalendarFilled,
  CalendarOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ContactsOutlined,
  EditOutlined,
  IdcardOutlined,
  LikeOutlined,
  PhoneOutlined,
  SaveOutlined,
  SmileOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Flex,
  Form,
  message,
  Row,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { memo, useState } from "react";
import { useParams } from "react-router-dom";
import Loading from "../../components/common/Loading";
import { IEmployerDetail } from "../../models/Hrm";
import { useGetEmployee, useUpdateEmployee } from "../../query/HrmQuery";
import DataUnavailable from "../Unavailable/DataUnavailable";
import HrmEditEmployeeForm from "./HrmEmployeeEditForm";

const { Title, Text } = Typography;

const HrmEmployeeInfo = () => {
  const { alias } = useParams<{ alias: string }>();

  const { data: employee, isLoading } = useGetEmployee(alias!);
  const updateEmployee = useUpdateEmployee();

  const [isEdit, setIsEdit] = useState(false);
  const [form] = Form.useForm();

  if (isLoading) return <Loading show />;

  if (!employee) {
    return <DataUnavailable />;
  }

  const handleEdit = () => {
    setIsEdit(true);
  };

  const handleCancel = () => {
    setIsEdit(false);
    form.resetFields();
  };

  const handleSubmit = (values: IEmployerDetail) => {
    if (!values.born) return;
    updateEmployee.mutate(
      {
        id: alias ?? "",
        employeeData: {
          employeeNo: values.employeeNo,
          phone: values.phone,
          addressCurrent: values.addressCurrent,
          addressPermanent: values.addressPermanent,
          gender: values.gender,
          born: values.born,
          hiredAt: employee.hiredAt,
          isWorking: values.isWorking,
          isOvertime: values.isOvertime,
          leaveBalance: values.leaveBalance,
        },
      },
      {
        onSuccess: () => {
          message.success("Cập nhật thông tin nhân viên thành công");
          setIsEdit(false);
        },
        onError: () => {
          message.error("Có lỗi xảy ra khi cập nhật!");
        },
      },
    );
  };

  const infoFields = [
    {
      label: "Mã nhân viên",
      icon: <IdcardOutlined />,

      value: employee.employeeNo,
    },
    {
      label: "Phòng ban",
      icon: <BankOutlined />,

      value: employee?.user?.department?.departmentName,
    },
    {
      label: "Ngày vào làm",
      icon: <CalendarOutlined />,

      value: dayjs(employee.hiredAt).format("DD/MM/YYYY"),
    },
    {
      label: "Số ngày phép",
      icon: <SmileOutlined />,

      value: `${employee.leaveBalance ?? 0} ngày`,
    },
    {
      label: "Số điện thoại",
      icon: <PhoneOutlined />,

      value: employee.phone || "Chưa cập nhật",
    },
    {
      label: "Ngày sinh",
      icon: <CalendarFilled />,

      value: employee.born
        ? dayjs(employee.born).format("DD/MM/YYYY")
        : "Chưa cập nhật",
    },
    {
      label: "Địa chỉ hiện tại",
      icon: <ContactsOutlined />,

      value: employee.addressCurrent || "Chưa cập nhật",
    },
    {
      label: "Địa chỉ thường trú",
      icon: <ContactsOutlined />,

      value: employee.addressPermanent || "Chưa cập nhật",
    },
  ];
  return (
    <Card
      className="mb-8 shadow-sm border-gray-100 rounded-2xl overflow-hidden"
      bodyStyle={{ padding: 0 }}
    >
      <div className="p-6 md:p-8">
        <Row gutter={[32, 32]}>
          {/* LEFT: PROFILE INFO */}
          <Col xs={24} md={8} lg={6}>
            <Flex vertical align="center" className="text-center">
              <div className="relative mb-4">
                <Avatar
                  size={100}
                  className="bg-blue-50 text-blue-600 text-4xl font-bold shadow-sm border-2 border-white ring-1 ring-gray-100"
                  icon={!employee.user.fullName && <UserOutlined />}
                  src={employee.user.avatar}
                >
                  {employee.user.fullName?.charAt(0).toUpperCase()}
                </Avatar>
                <Tag
                  color={
                    employee.gender === "MALE"
                      ? "blue"
                      : employee.gender === "FEMALE"
                        ? "magenta"
                        : "default"
                  }
                  className="absolute bottom-0 right-[-10px] m-0 border-white"
                >
                  {employee.gender === "MALE"
                    ? "Nam"
                    : employee.gender === "FEMALE"
                      ? "Nữ"
                      : "Khác"}
                </Tag>
              </div>

              <Title level={4} className="!mb-1 text-gray-800">
                {employee.user.fullName}
              </Title>

              <Text className="text-gray-500">{employee.user.email}</Text>

              <Tag color="purple" className="mt-2 px-3 py-1 rounded-full">
                {employee.user.position.positionTitle}
              </Tag>
              <Divider className="my-6 md:hidden" />
            </Flex>
          </Col>

          <Col hidden className="md:flex md:justify-center px-0">
            <Divider type="vertical" className="h-full" />
          </Col>

          <Col xs={24} md={15} lg={17}>
            <Flex justify="space-between" align="center" className="mb-6">
              <Title level={5} className="!m-0 text-gray-700">
                Thông tin chi tiết
              </Title>

              {!isEdit ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  className="rounded-md"
                >
                  Chỉnh sửa
                </Button>
              ) : (
                <Flex gap="small">
                  <Button icon={<CloseOutlined />} onClick={handleCancel}>
                    Huỷ
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => form.submit()}
                    loading={updateEmployee.isPending}
                  >
                    Lưu thay đổi
                  </Button>
                </Flex>
              )}
            </Flex>

            {!isEdit ? (
              <Descriptions
                column={{ xs: 1, sm: 2, lg: 2 }}
                layout="vertical"
                size="middle"
                className="p-5 rounded-2xl shadow-sm"
              >
                {infoFields.map((item) => (
                  <Descriptions.Item
                    key={item.label}
                    label={
                      <Text
                        className={`flex items-center gap-2 text-blue-500 font-medium`}
                      >
                        {item.icon}
                        {item.label}
                      </Text>
                    }
                  >
                    <Text>{item.value}</Text>
                  </Descriptions.Item>
                ))}
                <Descriptions.Item
                  label={
                    <Text
                      className={`flex items-center gap-2 text-blue-500 font-medium`}
                    >
                      <LikeOutlined />
                      Trạng thái làm việc
                    </Text>
                  }
                >
                  <Tag color={employee.isWorking ? "green" : "red"}>
                    {employee.isWorking ? "Đang làm việc" : "Nghỉ việc"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Text
                      className={`flex items-center gap-2 text-blue-500 font-medium`}
                    >
                      <ClockCircleOutlined />
                      Tăng ca
                    </Text>
                  }
                >
                  <Tag color={employee.isOvertime ? "green" : "red"}>
                    {employee.isOvertime
                      ? "Được phép tăng ca"
                      : "Không được phép"}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <HrmEditEmployeeForm
                employee={employee}
                form={form}
                onFinish={handleSubmit}
              />
            )}
          </Col>
        </Row>
      </div>
    </Card>
  );
};

export default memo(HrmEmployeeInfo);
